import { useState, useMemo, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { articles, categories } from '@/mocks/articles';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'guide');

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
    }
  }, [slug]);

  const currentCategory = useMemo(
    () => categories.find((c) => c.slug === activeSlug),
    [activeSlug],
  );

  const filteredArticles = useMemo(
    () => articles.filter((a) => a.categorySlug === activeSlug),
    [activeSlug],
  );

  return (
    <>
      <ScrollToTop />
      <main>
        <Navbar />

        <section className="pt-20 pb-10 bg-background-100">
          <div className="mx-auto px-4 md:px-6 max-w-[1400px]">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: '分类浏览' },
              ]}
            />
            <div className="text-center max-w-2xl mx-auto mt-6 mb-8">
              <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full mb-4">
                内容分类
              </span>
              <h1 className="text-3xl md:text-4xl font-extrabold text-foreground-950 mb-4">
                分类浏览
              </h1>
              <p className="text-foreground-600 text-sm md:text-base">
                选择您感兴趣的分类，浏览18c.mic的相关内容与资源。
              </p>
            </div>

            <div className="flex items-center justify-center flex-wrap gap-2 mb-10">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`whitespace-nowrap px-4 py-2 text-sm font-medium rounded-full transition-all cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-50 text-foreground-600 border border-background-200/70 hover:border-primary-300 hover:text-primary-600'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-14 bg-background-50">
          <div className="mx-auto px-4 md:px-6 max-w-[1400px]">
            {currentCategory && (
              <div className="mb-10 text-center">
                <div className="w-14 h-14 mx-auto flex items-center justify-center bg-accent-100 rounded-full mb-4">
                  <i className={`${currentCategory.icon} text-2xl text-accent-600`}></i>
                </div>
                <h2 className="text-xl font-bold text-foreground-950 mb-2">
                  {currentCategory.name}
                </h2>
                <p className="text-sm text-foreground-600 max-w-md mx-auto">
                  {currentCategory.description}
                </p>
              </div>
            )}

            {filteredArticles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredArticles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    className="rounded-lg bg-background-50 border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all cursor-pointer group"
                  >
                    <div className="w-full h-44 overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-3 mb-3 text-xs text-foreground-500 flex-wrap">
                        <span>{article.date}</span>
                        <span>{article.readTime}阅读</span>
                      </div>
                      <h3 className="text-base font-semibold text-foreground-950 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center gap-2 mt-3 flex-wrap">
                        {article.tags.map((tag) => (
                          <span
                            key={tag}
                            className="px-2 py-0.5 bg-secondary-100 text-secondary-700 text-xs rounded-full"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto flex items-center justify-center bg-background-100 rounded-full mb-4">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-600">该分类下暂无文章</p>
              </div>
            )}
          </div>
        </section>

        <Footer />
      </main>
    </>
  );
}